import type { AdminReviewListItemResponse } from "../../../types/adminTypes";
import { formatDateTime, formatDateTimeInGigTimeZone, formatEstimatedDuration } from "../../../utils/DateUtils";
import { formatGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";
import {
  getFullName,
  getReviewContextLabel,
  getReviewContextTone,
  getStatusTone,
} from "./adminReviewUtils";

type AdminReviewDetailModalProps = {
  review: AdminReviewListItemResponse | null;
  isOpen: boolean;
  onClose: () => void;
};

const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-2xl border border-myteengig-border bg-white px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">{label}</p>
    <p className="mt-2 break-words text-sm text-myteengig-text-primary">{value}</p>
  </div>
);

const AdminReviewDetailModal = ({ review, isOpen, onClose }: AdminReviewDetailModalProps) => {
  const reviewerName = review ? getFullName(review.reviewerFirstName, review.reviewerLastName, "Unknown reviewer") : "";
  const revieweeName = review ? getFullName(review.revieweeFirstName, review.revieweeLastName, "Unknown reviewee") : "";
  const teenName = review ? getFullName(review.teenFirstName, review.teenLastName, "Unknown teen") : "";
  const posterName = review ? getFullName(review.posterFirstName, review.posterLastName, "Unknown poster") : "";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={review ? `${review.gigTitle ?? "Review"} · Review detail` : "Review detail"} panelClassName="max-w-5xl">
      {review ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Rating</p>
              <div className="mt-3">
                <AdminPillBadge>{review.rating.toFixed(1)} / 5</AdminPillBadge>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Context</p>
              <div className="mt-3">
                <AdminPillBadge tone={getReviewContextTone(review.reviewContext)}>
                  {getReviewContextLabel(review.reviewContext)}
                </AdminPillBadge>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Review Created</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatDateTime(review.createdAt)}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Request Status</p>
              <div className="mt-3">
                {review.gigRequestStatus ? (
                  <AdminPillBadge tone={getStatusTone(review.gigRequestStatus)}>{review.gigRequestStatus}</AdminPillBadge>
                ) : (
                  <AdminPillBadge>Unknown</AdminPillBadge>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Review</p>
            <p className="mt-3 whitespace-pre-wrap break-words text-sm text-myteengig-text-muted">
              {review.message?.trim() || "No written feedback was included with this review."}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <DetailItem label="Reviewer" value={`${reviewerName}${review.reviewerEmail ? ` · ${review.reviewerEmail}` : ""}`} />
            <DetailItem label="Reviewee" value={`${revieweeName}${review.revieweeEmail ? ` · ${review.revieweeEmail}` : ""}`} />
            <DetailItem label="Teen" value={`${teenName}${review.teenEmail ? ` · ${review.teenEmail}` : ""}`} />
            <DetailItem label="Poster" value={`${posterName}${review.posterEmail ? ` · ${review.posterEmail}` : ""}`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-sm font-semibold text-myteengig-text-primary">Gig</p>
                {review.gigStatus ? <AdminPillBadge tone={getStatusTone(review.gigStatus)}>{review.gigStatus}</AdminPillBadge> : null}
              </div>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p className="font-medium text-myteengig-text-primary">{review.gigTitle ?? "Untitled gig"}</p>
                <p>{review.gigCategory ?? "No category"}</p>
                <p>{formatCityStateZip({ city: review.gigCity, state: review.gigState, zip: review.gigZip })}</p>
                <p>{formatGigCompensation(review.gigPaymentAmountCents, review.volunteer)}</p>
                <p>Duration: {formatEstimatedDuration(review.gigEstimatedDurationMinutes)}</p>
                <p>Starts: {formatDateTimeInGigTimeZone(review.gigStartTime, review.gigTimeZone)}</p>
                <p>Ends: {formatDateTimeInGigTimeZone(review.gigEndTime, review.gigTimeZone)}</p>
                <p>Created: {formatDateTime(review.gigCreatedAt)}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="text-sm font-semibold text-myteengig-text-primary">Gig Request</p>
                {review.gigRequestStatus ? <AdminPillBadge tone={getStatusTone(review.gigRequestStatus)}>{review.gigRequestStatus}</AdminPillBadge> : null}
              </div>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>Proposed start: {formatDateTimeInGigTimeZone(review.proposedStartTime, review.gigTimeZone)}</p>
                <p>Proposed end: {formatDateTimeInGigTimeZone(review.proposedEndTime, review.gigTimeZone)}</p>
                <p>Confirmed start: {formatDateTimeInGigTimeZone(review.confirmedStartTime, review.gigTimeZone)}</p>
                <p>Confirmed end: {formatDateTimeInGigTimeZone(review.confirmedEndTime, review.gigTimeZone)}</p>
                <p>Teen arrived: {formatDateTime(review.teenArrivedAt)}</p>
                <p>Teen complete: {formatDateTime(review.teenMarkedCompletedAt)}</p>
                <p>Adult complete: {formatDateTime(review.adultConfirmedCompletedAt)}</p>
                <p>Requested: {formatDateTime(review.requestCreatedAt)}</p>
              </div>
              <div className="mt-4 rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Original Request Message</p>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">
                  {review.requestMessage?.trim() || "No request message was included."}
                </p>
              </div>
            </div>
          </div>

        </div>
      ) : null}
    </Modal>
  );
};

export default AdminReviewDetailModal;
